import { useMemo } from "react";
import { useCrm, collabById } from "@/lib/crm";
import { RitrCanali } from "@/components/RitrCanali";
import { eur, MONTHLY_TARGETS, STAGES, dateIt, compensoSsd, bonusCount, bonusUnit, isAcq } from "@/lib/constants";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Target, TrendingUp, Wallet, Trophy, CalendarClock, ArrowUpRight } from "lucide-react";

function Stat({ icon: Icon, label, value, sub, tid, accent }) {
  return (
    <Card data-testid={tid} className="p-5 flex flex-col gap-3 hover:-translate-y-0.5 transition-transform duration-200 animate-fade-up">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium uppercase tracking-wider text-slate-400">{label}</span>
        <span className={`h-8 w-8 rounded-lg flex items-center justify-center ${accent}`}>
          <Icon className="h-4 w-4" />
        </span>
      </div>
      <div className="text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white tabular-nums">{value}</div>
      {sub && <div className="text-xs text-slate-500">{sub}</div>}
    </Card>
  );
}

export default function Dashboard() {
  const { ssd, collaboratori, settings, loading, progetti } = useCrm();

  const stats = useMemo(() => {
    const acquired = ssd.filter((s) => isAcq(s, settings));
    const totalRevenue = acquired.reduce((a, s) => a + (s.ricavo || 0), 0);
    const comm = settings?.commissione ?? 0.5;
    const target = settings?.bonus_target_ssd ?? 15;
    const compenso = acquired.reduce((a, s) => a + compensoSsd(s, collabById(collaboratori, s.collaboratore_id), comm, settings), 0);

    const perCollab = collaboratori.map((c) => {
      const acq = acquired.filter((s) => s.collaboratore_id === c.id);
      const rev = acq.reduce((a, s) => a + (s.ricavo || 0), 0);
      const share = acq.reduce((a, s) => a + compensoSsd(s, c, comm, settings), 0);
      const count = bonusCount(acq, settings);
      return { c, count, rev, share, bonus: target > 0 && count >= target };
    });

    const targets = settings?.target_mensili?.length ? settings.target_mensili : MONTHLY_TARGETS;
    const monthly = targets.map((m) => {
      const got = acquired.filter((s) => (s.data_acquisizione || "").slice(0, 7) === m.ym).length;
      return { ...m, got };
    });

    return { acquired, totalRevenue, compenso, perCollab, monthly, target };
  }, [ssd, collaboratori, settings]);

  const byStage = useMemo(
    () => STAGES.map((s) => ({ ...s, n: ssd.filter((x) => x.stage === s.key).length })),
    [ssd]
  );

  if (loading) return <div className="p-10 text-slate-400">Caricamento…</div>;

  const totalBonusProgress = Math.min(100, (stats.acquired.length / (stats.target * 2)) * 100);

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 dark:text-white">Dashboard</h1>
        <p className="text-slate-500 mt-1">Panoramica acquisizioni {settings?.settore === "ritrattistica" ? "clienti" : "SSD"}, ricavi e compensi collaboratori.</p>
      </div>

      {settings?.settore === "ritrattistica" && <RitrCanali ssd={ssd} progetti={progetti} settings={settings} />}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat tid="kpi-total-ssd-count" icon={Target} label={settings?.settore === "ritrattistica" ? "Clienti Acquisiti" : "SSD Acquisite"} accent="bg-emerald-100 text-emerald-600 dark:bg-emerald-500/15"
          value={stats.acquired.length} sub={`${ssd.length} totali in pipeline`} />
        <Stat tid="kpi-total-revenue" icon={TrendingUp} label="Ricavi Totali" accent="bg-sky-100 text-sky-600 dark:bg-sky-500/15"
          value={eur(stats.totalRevenue)} sub="Da contratti chiusi" />
        <Stat tid="kpi-collaborator-compensation" icon={Wallet} label="Compensi" accent="bg-primary/15 text-primary"
          value={eur(stats.compenso)} sub="Totale dovuto collaboratori" />
        <Stat tid="kpi-bonus-progress" icon={Trophy} label={`Bonus ${eur(settings?.bonus_importo ?? 1000)}`} accent="bg-amber-100 text-amber-600 dark:bg-amber-500/15"
          value={`${stats.perCollab.filter((p) => p.bonus).length}/${collaboratori.length}`} sub={`Traguardo ${stats.target} ${bonusUnit(settings)} cad.`} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Monthly targets */}
        <Card className="lg:col-span-2 p-6 animate-fade-up">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Obiettivo mensile acquisizioni</h2>
              <p className="text-xs text-slate-500">3/mese Ott–Dic 2026 · 1/mese Gen–Mag 2027</p>
            </div>
            <Target className="h-5 w-5 text-slate-300" />
          </div>
          <div className="space-y-3">
            {stats.monthly.map((m) => {
              const pct = Math.min(100, (m.got / m.target) * 100);
              const done = m.got >= m.target;
              return (
                <div key={m.ym} className="flex items-center gap-3">
                  <span className="w-14 text-xs font-medium text-slate-500">{m.label}</span>
                  <div className="flex-1 h-2.5 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                    <div className={`h-full rounded-full transition-all duration-500 ${done ? "bg-emerald-500" : "bg-primary"}`} style={{ width: `${pct}%` }} />
                  </div>
                  <span className={`w-12 text-right text-xs font-semibold tabular-nums ${done ? "text-emerald-600" : "text-slate-600 dark:text-slate-300"}`}>
                    {m.got}/{m.target}
                  </span>
                </div>
              );
            })}
          </div>
        </Card>

        {/* Pipeline breakdown */}
        <Card className="p-6 animate-fade-up">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-5">Pipeline per fase</h2>
          <div className="space-y-4">
            {byStage.map((s) => (
              <div key={s.key} className="flex items-center gap-3">
                <span className={`h-2.5 w-2.5 rounded-full ${s.dot}`} />
                <span className="flex-1 text-sm text-slate-600 dark:text-slate-300">{s.label}</span>
                <span className="text-sm font-bold tabular-nums text-slate-900 dark:text-white">{s.n}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Bonus progress bar */}
      <Card className="p-6 animate-fade-up">
        <div className="flex items-center gap-2 mb-4">
          <Trophy className="h-5 w-5 text-amber-500" />
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Traguardo Bonus — {eur(settings?.bonus_importo || 1000)} cad. entro {dateIt(settings?.bonus_scadenza)}</h2>
        </div>
        <div className="grid sm:grid-cols-2 gap-6">
          {stats.perCollab.map(({ c, count, bonus }) => {
            const pct = Math.min(100, (count / stats.target) * 100);
            return (
              <div key={c.id}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-slate-700 dark:text-slate-200">{c.nome}</span>
                  {bonus
                    ? <Badge className="bg-emerald-500 hover:bg-emerald-500">Bonus raggiunto 🎉</Badge>
                    : <span className="text-xs text-slate-500 tabular-nums">{count}/{stats.target} {bonusUnit(settings)}</span>}
                </div>
                <Progress value={pct} className="h-2.5" />
              </div>
            );
          })}
        </div>
      </Card>

      {/* Payout schedule */}
      <Card className="p-6 animate-fade-up">
        <div className="flex items-center gap-2 mb-4">
          <CalendarClock className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Date di liquidazione compensi</h2>
        </div>
        <div className="grid sm:grid-cols-3 gap-4">
          {(settings?.date_liquidazione || []).map((d, i) => (
            <div key={i} className="rounded-xl border border-slate-200 dark:border-slate-800 p-4 bg-slate-50 dark:bg-slate-900/50">
              <div className="text-xs uppercase tracking-wider text-slate-400">Rata {i + 1}</div>
              <div className="text-xl font-bold text-slate-900 dark:text-white mt-1">{dateIt(d)}</div>
            </div>
          ))}
        </div>
        <p className="text-xs text-slate-400 mt-3 flex items-center gap-1">
          <ArrowUpRight className="h-3 w-3" /> Modifica le date nella sezione Collaboratori.
        </p>
      </Card>
    </div>
  );
}
